import { getAdminServicePromiseClient } from '../utils/grpcClient';
import {
  GetPendingAgenciesRequest,
  GetPendingAgenciesResponse,
  ApproveAgencyRequest,
  ApproveAgencyResponse,
  RejectAgencyRequest,
  RejectAgencyResponse,
  PendingAgency,
  GetAdminStatsRequest,
  GetAdminStatsResponse,
  AdminStats,
  GetAllAgenciesRequest,
  GetAllAgenciesResponse,
  AgencyInfo,
  GetAgencyDetailsRequest,
  GetAgencyDetailsResponse,
  AgencyDetails,
  GetIndependentTrainersRequest,
  TrainerSummary,
  SetAgencyBrandingRequest
} from '../generated/fitness-service/v1/admin_pb';

export interface PendingAgencyData {
  id: number;
  name: string;
  description: string;
  address: string;
  contact_phone?: string;
  contact_email: string;
  created_at: string;
  owner: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
}

export interface ApproveAgencyResult {
  success: boolean;
  message: string;
}

export interface RejectAgencyResult {
  success: boolean;
  message: string;
}

export interface AdminStatsData {
  total_agencies: number;
  active_agencies: number;
  pending_agencies: number;
  total_trainers: number;
  active_trainers: number;
  total_clients: number;
  active_clients: number;
  total_workouts: number;
  total_exercises: number;
  independent_trainers: number;
  active_independent_trainers: number;
  new_independent_trainers_30d: number;
}

export interface AgencyInfoData {
  id: number;
  name: string;
  description: string;
  address: string;
  contact_phone?: string;
  contact_email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  owner: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  trainer_count: number;
  client_count: number;
  last_activity?: string;
  is_independent: boolean;
}

export interface GetAllAgenciesParams {
  page?: number;
  limit?: number;
  active_only?: boolean;
  search?: string;
}

export interface GetAllAgenciesResult {
  agencies: AgencyInfoData[];
  total_count: number;
  page: number;
  limit: number;
}

export interface TrainerSummaryData {
  id: number;
  user_id: number;
  name: string;
  email: string;
  phone: string;
  specialization: string;
  certification: string;
  experience_years: number;
  trainer_code: string;
  instagram_handle: string;
  is_active: boolean;
  is_independent: boolean;
  client_count: number;
  join_date: string;
  last_login?: string;
  agency_id: number;
  branding_enabled: boolean;
}

export interface GetIndependentTrainersParams {
  page?: number;
  limit?: number;
  active_only?: boolean;
  search?: string;
}

export interface GetIndependentTrainersResult {
  trainers: TrainerSummaryData[];
  total_count: number;
  page: number;
  limit: number;
}

// Shared mapper so agency details and the independent trainer list stay in sync
const toTrainerSummaryData = (trainer: TrainerSummary): TrainerSummaryData => {
  const joinDate = trainer.getJoinDate();
  const lastLogin = trainer.getLastLogin();

  return {
    id: trainer.getId(),
    user_id: trainer.getUserId(),
    name: trainer.getName(),
    email: trainer.getEmail(),
    phone: trainer.getPhone(),
    specialization: trainer.getSpecialization(),
    certification: trainer.getCertification(),
    experience_years: trainer.getExperienceYears(),
    trainer_code: trainer.getTrainerCode(),
    instagram_handle: trainer.getInstagramHandle(),
    is_active: trainer.getIsActive(),
    is_independent: trainer.getIsIndependent(),
    client_count: trainer.getClientCount(),
    join_date: joinDate ? joinDate.toDate().toISOString() : new Date().toISOString(),
    last_login: lastLogin ? lastLogin.toDate().toISOString() : undefined,
    agency_id: trainer.getAgencyId(),
    branding_enabled: trainer.getBrandingEnabled(),
  };
};

export interface AgencyStatsData {
  total_trainers: number;
  active_trainers: number;
  total_clients: number;
  active_clients: number;
  total_workouts: number;
  monthly_workouts: number;
  total_exercises: number;
}

export interface AgencyDetailsData {
  id: number;
  name: string;
  description: string;
  address: string;
  contact_phone?: string;
  contact_email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  owner: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  trainers: TrainerSummaryData[];
  agency_stats: AgencyStatsData;
  branding_enabled: boolean;
  brand_name: string;
}

export class AdminService {
  private client = getAdminServicePromiseClient();

  private getAuthMetadata() {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No access token found');
    }
    return { authorization: `Bearer ${token}` };
  }

  async getPendingAgencies(): Promise<PendingAgencyData[]> {
    try {
      const request = new GetPendingAgenciesRequest();
      const response = await this.client.getPendingAgencies(request, this.getAuthMetadata());

      return response.getAgenciesList().map((agency: PendingAgency) => {
        const owner = agency.getOwner();
        const createdAt = agency.getCreatedAt();

        return {
          id: agency.getId(),
          name: agency.getName(),
          description: agency.getDescription(),
          address: agency.getAddress(),
          contact_phone: agency.getContactPhone() || undefined,
          contact_email: agency.getContactEmail(),
          created_at: createdAt ? createdAt.toDate().toISOString() : new Date().toISOString(),
          owner: {
            id: owner!.getId(),
            first_name: owner!.getFirstName(),
            last_name: owner!.getLastName(),
            email: owner!.getEmail(),
          }
        };
      });
    } catch (error: any) {
      console.error('Failed to get pending agencies:', error);
      throw new Error(error.message || 'Failed to load pending agencies');
    }
  }

  async approveAgency(agencyId: number): Promise<ApproveAgencyResult> {
    try {
      const request = new ApproveAgencyRequest();
      request.setAgencyId(agencyId);

      const response = await this.client.approveAgency(request, this.getAuthMetadata());

      return {
        success: response.getSuccess(),
        message: response.getMessage()
      };
    } catch (error: any) {
      console.error('Failed to approve agency:', error);
      throw new Error(error.message || 'Failed to approve agency');
    }
  }

  async rejectAgency(agencyId: number, reason?: string): Promise<RejectAgencyResult> {
    try {
      const request = new RejectAgencyRequest();
      request.setAgencyId(agencyId);
      if (reason) {
        request.setReason(reason);
      }

      const response = await this.client.rejectAgency(request, this.getAuthMetadata());

      return {
        success: response.getSuccess(),
        message: response.getMessage()
      };
    } catch (error: any) {
      console.error('Failed to reject agency:', error);
      throw new Error(error.message || 'Failed to reject agency');
    }
  }

  async getAdminStats(): Promise<AdminStatsData> {
    try {
      const request = new GetAdminStatsRequest();
      const response = await this.client.getAdminStats(request, this.getAuthMetadata());

      const stats = response.getStats()!;
      return {
        total_agencies: stats.getTotalAgencies(),
        active_agencies: stats.getActiveAgencies(),
        pending_agencies: stats.getPendingAgencies(),
        total_trainers: stats.getTotalTrainers(),
        active_trainers: stats.getActiveTrainers(),
        total_clients: stats.getTotalClients(),
        active_clients: stats.getActiveClients(),
        total_workouts: stats.getTotalWorkouts(),
        total_exercises: stats.getTotalExercises(),
        independent_trainers: stats.getIndependentTrainers(),
        active_independent_trainers: stats.getActiveIndependentTrainers(),
        new_independent_trainers_30d: stats.getNewIndependentTrainers30d(),
      };
    } catch (error: any) {
      console.error('Failed to get admin stats:', error);
      throw new Error(error.message || 'Failed to load admin statistics');
    }
  }

  async getAllAgencies(params: GetAllAgenciesParams = {}): Promise<GetAllAgenciesResult> {
    try {
      const request = new GetAllAgenciesRequest();
      request.setPage(params.page || 1);
      request.setLimit(params.limit || 20);
      request.setActiveOnly(params.active_only ?? false);
      if (params.search) {
        request.setSearch(params.search);
      }

      const response = await this.client.getAllAgencies(request, this.getAuthMetadata());

      const agencies = response.getAgenciesList().map((agency: AgencyInfo) => {
        const owner = agency.getOwner();
        const createdAt = agency.getCreatedAt();
        const updatedAt = agency.getUpdatedAt();
        const lastActivity = agency.getLastActivity();

        return {
          id: agency.getId(),
          name: agency.getName(),
          description: agency.getDescription(),
          address: agency.getAddress(),
          contact_phone: agency.getContactPhone() || undefined,
          contact_email: agency.getContactEmail(),
          is_active: agency.getIsActive(),
          created_at: createdAt ? createdAt.toDate().toISOString() : new Date().toISOString(),
          updated_at: updatedAt ? updatedAt.toDate().toISOString() : new Date().toISOString(),
          owner: {
            id: owner!.getId(),
            first_name: owner!.getFirstName(),
            last_name: owner!.getLastName(),
            email: owner!.getEmail(),
          },
          trainer_count: agency.getTrainerCount(),
          client_count: agency.getClientCount(),
          last_activity: lastActivity ? lastActivity.toDate().toISOString() : undefined,
          is_independent: agency.getIsIndependent(),
        };
      });

      return {
        agencies,
        total_count: response.getTotalCount(),
        page: response.getPage(),
        limit: response.getLimit(),
      };
    } catch (error: any) {
      console.error('Failed to get all agencies:', error);
      throw new Error(error.message || 'Failed to load agencies');
    }
  }

  async getAgencyDetails(agencyId: number): Promise<AgencyDetailsData> {
    try {
      const request = new GetAgencyDetailsRequest();
      request.setAgencyId(agencyId);

      const response = await this.client.getAgencyDetails(request, this.getAuthMetadata());
      const agency = response.getAgency()!;
      const owner = agency.getOwner()!;
      const stats = agency.getAgencyStats()!;

      const trainers = agency.getTrainersList().map(toTrainerSummaryData);

      return {
        id: agency.getId(),
        name: agency.getName(),
        description: agency.getDescription(),
        address: agency.getAddress(),
        contact_phone: agency.getContactPhone() || undefined,
        contact_email: agency.getContactEmail(),
        is_active: agency.getIsActive(),
        created_at: agency.getCreatedAt()!.toDate().toISOString(),
        updated_at: agency.getUpdatedAt()!.toDate().toISOString(),
        owner: {
          id: owner.getId(),
          first_name: owner.getFirstName(),
          last_name: owner.getLastName(),
          email: owner.getEmail(),
        },
        trainers,
        agency_stats: {
          total_trainers: stats.getTotalTrainers(),
          active_trainers: stats.getActiveTrainers(),
          total_clients: stats.getTotalClients(),
          active_clients: stats.getActiveClients(),
          total_workouts: stats.getTotalWorkouts(),
          monthly_workouts: stats.getMonthlyWorkouts(),
          total_exercises: stats.getTotalExercises(),
        },
        branding_enabled: agency.getBrandingEnabled(),
        brand_name: agency.getBrandName(),
      };
    } catch (error: any) {
      console.error('Failed to get agency details:', error);
      throw new Error(error.message || 'Failed to load agency details');
    }
  }

  /// Turn the branded-app entitlement on or off for an agency (billing gate).
  async setAgencyBranding(agencyId: number, enabled: boolean): Promise<boolean> {
    try {
      const request = new SetAgencyBrandingRequest();
      request.setAgencyId(agencyId);
      request.setEnabled(enabled);

      const response = await this.client.setAgencyBranding(request, this.getAuthMetadata());
      return response.getBrandingEnabled();
    } catch (error: any) {
      console.error('Failed to set agency branding:', error);
      throw new Error(error.message || 'Failed to update branded-app access');
    }
  }

  async getIndependentTrainers(params: GetIndependentTrainersParams = {}): Promise<GetIndependentTrainersResult> {
    try {
      const request = new GetIndependentTrainersRequest();
      request.setPage(params.page || 1);
      request.setLimit(params.limit || 20);
      request.setActiveOnly(params.active_only ?? false);
      if (params.search) {
        request.setSearch(params.search);
      }

      const response = await this.client.getIndependentTrainers(request, this.getAuthMetadata());

      return {
        trainers: response.getTrainersList().map(toTrainerSummaryData),
        total_count: response.getTotalCount(),
        page: response.getPage(),
        limit: response.getLimit(),
      };
    } catch (error: any) {
      console.error('Failed to get independent trainers:', error);
      throw new Error(error.message || 'Failed to load independent trainers');
    }
  }
}

export const adminService = new AdminService();